/** packages/mouse-management/src/pages/performance/SensorModeCard.tsx */
import React from "react";
import { useTranslation } from "react-i18next";
import {
  officeModeImg,
  esportModeImg,
  berserkModeImg,
} from "@eweadn-qiankun/shared-assets";
import { Iconfont, useMessage } from "@eweadn-qiankun/shared-components";
import { trackClick } from "@eweadn-qiankun/shared-utils";
import "./SensorModeCard.scss";

interface SensorModeCardProps {
  sensorMode: 0 | 1 | 256;
  onSensorModeChange: (mode: 0 | 1 | 256) => void;
  /** 有线连接时仅狂暴模式可用，LP/HP 禁用 */
  isWired?: boolean;
  /**
   * full：办公/电竞/狂暴（匠盟等）
   * officeBerserk：仅办公+狂暴（芯永信对齐公版「长续航 / 火力全开」）
   */
  variant?: "full" | "officeBerserk";
}

const SensorModeCard: React.FC<SensorModeCardProps> = ({
  sensorMode,
  onSensorModeChange,
  isWired,
  variant = "full",
}) => {
  const { t } = useTranslation();
  const toast = useMessage();
  const showEsports = variant !== "officeBerserk";
  // 双模式方案：历史电竞(1) 视同办公(0)
  const activeMode: 0 | 1 | 256 =
    !showEsports && sensorMode === 1 ? 0 : sensorMode;

  const notifyLocked = (wired: boolean | undefined) => {
    // 延后 toast，避免与 qiankun/React 提交阶段叠 DOM 触发 removeChild
    const msg = wired
      ? t("mouse.performance.sensorModeWired")
      : t("mouse.performance.sensorModeAlreadyActive");
    window.setTimeout(() => toast.info(msg), 0);
  };

  const onModeClick = (target: 0 | 1 | 256) => {
    if (!showEsports && target === 1) return;

    const lpLocked = isWired || activeMode === 0;
    const hpLocked = isWired || activeMode === 1;
    const cordedLocked = activeMode === 256 || isWired;

    if (target === 0) {
      if (!lpLocked) {
        onSensorModeChange(0);
        trackClick({
          moduleId: "mouse-management/performance",
          elementId: "performance/mode",
          appType: "mouse",
          deviceType: "mouse",
          extra: { mode: 0 },
        });
        return;
      }
      notifyLocked(isWired);
      return;
    }
    if (target === 1) {
      if (!hpLocked) {
        onSensorModeChange(1);
        trackClick({
          moduleId: "mouse-management/performance",
          elementId: "performance/mode",
          appType: "mouse",
          deviceType: "mouse",
          extra: { mode: 1 },
        });
        return;
      }
      notifyLocked(isWired);
      return;
    }
    if (!cordedLocked) {
      onSensorModeChange(256);
      trackClick({
        moduleId: "mouse-management/performance",
        elementId: "performance/mode",
        appType: "mouse",
        deviceType: "mouse",
        extra: { mode: 256 },
      });
      return;
    }
    notifyLocked(isWired);
  };

  const attrsFor = (target: 0 | 1 | 256) => {
    const locked =
      target === 0
        ? isWired || activeMode === 0
        : target === 1
          ? isWired || activeMode === 1
          : activeMode === 256 || isWired;
    return {
      "aria-disabled": locked,
      className:
        target === 0
          ? `mode-button mode-lp ${activeMode === 0 ? "active" : ""}`
          : target === 1
            ? `mode-button mode-hp ${activeMode === 1 ? "active" : ""}`
            : `mode-button mode-corded ${activeMode === 256 ? "active" : ""}`,
    };
  };

  return (
    <div
      className={`sensor-mode-card${
        variant === "officeBerserk" ? " sensor-mode-card--two" : ""
      }`}
    >
      <div className="card-header">
        <h3 className="card-title">{t("mouse.performance.sensorMode")}</h3>
        <p className="card-description">
          {t("mouse.performance.sensorModeDesc")}
        </p>
      </div>
      <div className="sensor-mode-content">
        <div className="mode-options">
          <button type="button" {...attrsFor(0)} onClick={() => onModeClick(0)}>
            <img
              src={officeModeImg}
              alt={t("mouse.performance.lpModeTitle")}
              className="mode-bg-img"
            />
            <Iconfont icon="check" className="mode-check-icon" />
            <div className="mode-text-container">
              <div className="mode-icon-placeholder">
                <Iconfont icon="officemode" className="mode-icon" />
              </div>
              <div className="mode-label">
                {t("mouse.performance.lpModeTitle")}
              </div>
              <div className="mode-description">
                {t("mouse.performance.lpModeDesc")}
              </div>
            </div>
          </button>

          {showEsports ? (
            <button
              type="button"
              {...attrsFor(1)}
              onClick={() => onModeClick(1)}
            >
              <img
                src={esportModeImg}
                alt={t("mouse.performance.hpModeTitle")}
                className="mode-bg-img"
              />
              <Iconfont icon="check" className="mode-check-icon" />
              <div className="mode-text-container">
                <div className="mode-icon-placeholder">
                  <Iconfont icon="esportmode" className="mode-icon" />
                </div>
                <div className="mode-label">
                  {t("mouse.performance.hpModeTitle")}
                </div>
                <div className="mode-description">
                  {t("mouse.performance.hpModeDesc")}
                </div>
              </div>
            </button>
          ) : null}

          <button
            type="button"
            {...attrsFor(256)}
            onClick={() => onModeClick(256)}
          >
            <img
              src={berserkModeImg}
              alt={t("mouse.performance.cordedModeTitle")}
              className="mode-bg-img"
            />
            <Iconfont icon="check" className="mode-check-icon" />
            <div className="mode-text-container">
              <div className="mode-icon-placeholder">
                <Iconfont icon="berserkmode" className="mode-icon" />
              </div>
              <div className="mode-label">
                {t("mouse.performance.cordedModeTitle")}
              </div>
              <div className="mode-description">
                {t("mouse.performance.cordedModeDesc")}
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SensorModeCard;
